import type { TileMap } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { POOL_FLOOR, tileShape } from './arenaMesh';

/** The named materials a tile family may ask for (`material` in the tileset) that are liquids. */
export type LiquidKind = 'water' | 'lava';
const KINDS: readonly LiquidKind[] = ['water', 'lava'];
/** Where a liquid's surface lies: a hair over the sunken floor `arenaMesh.ts` draws under it. */
export const SURFACE_Y = POOL_FLOOR + 0.01;
/**
 * Where it lies on ground a walker crosses, as lava does (D-43): a hair over the walking plane,
 * the ground's own top drawn under it.
 */
export const FLAT_SURFACE_Y = 0.01;
const SIZE = 64;
/** Lava's own picture, molten veins over a dark crust (ambientCG Lava005, CC0). */
const LAVA_TEXTURE = '/textures/arena/lava.webp';
/** Lava's tint, redder than the picture, and how far and how fast its glow throbs. */
const LAVA_GLOW = new Color3(1, 0.78, 0.55);
const LAVA_THROB = 0.08;
const LAVA_THROB_RATE = 1.3;

export interface LiquidTile {
  x: number;
  z: number;
  /** The surface's height: over a sunken pool's floor, or flat on the ground a walker crosses. */
  y: number;
}

/** The tiles of each liquid material, in row order. */
export function liquidTiles(
  map: Pick<TileMap, 'width' | 'height' | 'tile'>,
): Record<LiquidKind, LiquidTile[]> {
  const out: Record<LiquidKind, LiquidTile[]> = { water: [], lava: [] };
  for (let y = 0; y < map.height; y++)
    for (let x = 0; x < map.width; x++) {
      const tile = map.tile(x, y);
      const kind = tile.material;
      if (kind !== 'water' && kind !== 'lava') continue;
      const surface = tileShape(tile).kind === 'pool' ? SURFACE_Y : FLAT_SURFACE_Y;
      out[kind].push({ x, z: y, y: surface });
    }
  return out;
}

/**
 * Soft ripples as a normal map: two crossing waves that repeat seamlessly across the texture,
 * worked out by hand so no noise library or image file is needed.
 */
function rippleTexture(scene: Scene, name: string): Texture {
  const data = new Uint8Array(SIZE * SIZE * 4);
  const tau = Math.PI * 2;
  const steep = 0.08;
  for (let y = 0; y < SIZE; y++)
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE;
      const v = y / SIZE;
      // The slopes of sin(2πu·2 + 2πv) + sin(2πu·3 − 2πv·2), along u and along v.
      const a = Math.cos(tau * (u * 2 + v));
      const b = Math.cos(tau * (u * 3 - v * 2));
      const du = (a * 2 + b * 3) * steep;
      const dv = (a - b * 2) * steep;
      const length = Math.hypot(du, dv, 1);
      const i = (y * SIZE + x) * 4;
      data[i] = Math.round((-du / length / 2 + 0.5) * 255);
      data[i + 1] = Math.round((-dv / length / 2 + 0.5) * 255);
      data[i + 2] = Math.round((1 / length / 2 + 0.5) * 255);
      data[i + 3] = 255;
    }
  const texture = RawTexture.CreateRGBATexture(
    data,
    SIZE,
    SIZE,
    scene,
    true,
    false,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  texture.name = name;
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  return texture;
}

/**
 * The surfaces of the arena's liquids, in the material their tile family names
 * (content:quality#2, x-aaa-gap#10), where the renderer drew the atlas's flat blue or orange
 * square: water a clear, rippling surface that glints in the sun over its sunken floor; lava a
 * slow churn that glows by its own light, bright by night as by day. One mesh per material an
 * arena has, and so a draw call each; no mesh for one it has not.
 */
export class LiquidSurfaces {
  private readonly surfaces: {
    mesh: Mesh;
    material: StandardMaterial;
    ripples: Texture | null;
    speed: number;
    /** Lava's picture, drifting with its churn. */
    flow: Texture | null;
  }[] = [];
  private clock = 0;

  constructor(
    scene: Scene,
    parent: TransformNode,
    map: Pick<TileMap, 'width' | 'height' | 'tile'>,
  ) {
    const tiles = liquidTiles(map);
    for (const kind of KINDS) {
      if (tiles[kind].length === 0) continue;
      const mesh = surfaceMesh(scene, `arenaLiquid-${kind}`, tiles[kind]);
      mesh.parent = parent;
      mesh.isPickable = false;
      const material = new StandardMaterial(`arenaLiquid-${kind}`, scene);
      let ripples: Texture | null = null;
      let flow: Texture | null = null;
      if (kind === 'water') {
        ripples = rippleTexture(scene, `arenaLiquid-${kind}`);
        ripples.uScale = ripples.vScale = 0.25;
        material.bumpTexture = ripples;
        material.diffuseColor = new Color3(0.16, 0.36, 0.5);
        material.specularColor = new Color3(0.7, 0.75, 0.8);
        material.specularPower = 96;
        material.alpha = 0.55;
      } else {
        // Molten veins over a dark crust, drawn by their own light alone: bright by night as by
        // day, and never washed pale by the sun or a lamp. It creeps and throbs slowly (D-43:
        // "red structure lava animation"). One repeat every two tiles.
        flow = new Texture(LAVA_TEXTURE, scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);
        flow.uScale = flow.vScale = 0.5;
        // Unlit, a standard material shows its picture times its emissive colour, the tint here;
        // an emissive picture would be added to that colour instead, and wash it white.
        material.diffuseTexture = flow;
        material.emissiveColor = LAVA_GLOW.clone();
        material.disableLighting = true;
      }
      mesh.material = material;
      this.surfaces.push({ mesh, material, ripples, speed: kind === 'water' ? 0.05 : 0.015, flow });
    }
  }

  /** Call once per rendered frame, with the duel's shown time: the ripples drift. */
  update(dt: number): void {
    this.clock += dt;
    for (const { material, ripples, speed, flow } of this.surfaces) {
      if (ripples) {
        ripples.uOffset = (this.clock * speed) % 1;
        ripples.vOffset = (this.clock * speed * 0.6) % 1;
      }
      if (flow) {
        flow.uOffset = (this.clock * speed * 0.5) % 1;
        flow.vOffset = (this.clock * speed * -0.35) % 1;
        const throb = 1 - LAVA_THROB * (0.5 + 0.5 * Math.sin(this.clock * LAVA_THROB_RATE));
        material.emissiveColor.copyFrom(LAVA_GLOW).scaleInPlace(throb);
      }
    }
  }

  get meshes(): Mesh[] {
    return this.surfaces.map((surface) => surface.mesh);
  }

  dispose(): void {
    for (const { mesh, material, ripples, flow } of this.surfaces) {
      ripples?.dispose();
      flow?.dispose();
      material.dispose();
      mesh.dispose();
    }
    this.surfaces.length = 0;
  }
}

/** One flat quad per tile at the liquid's surface, all in one mesh. */
function surfaceMesh(scene: Scene, name: string, tiles: LiquidTile[]): Mesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (const { x, z, y } of tiles) {
    const base = positions.length / 3;
    for (const [dx, dz] of [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ] as const) {
      positions.push(x + dx, y, z + dz);
      normals.push(0, 1, 0);
      // In tiles, so neighbouring quads carry the ripples on without a seam.
      uvs.push(x + dx, z + dz);
    }
    // Wound as the floor's top faces are in `arenaMesh.ts`, so it faces up.
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const mesh = new Mesh(name, scene);
  const data = new VertexData();
  data.positions = positions;
  data.normals = normals;
  data.uvs = uvs;
  data.indices = indices;
  data.applyToMesh(mesh, false);
  return mesh;
}
